import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import { SKILLS } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';
import type { Profile } from '../types/database';

const editMemberSchema = z.object({
  role: z.enum(['admin', 'member'], {
    required_error: 'Please select a role',
  }),
  instruments: z.array(z.string()).min(1, 'Please select at least one instrument/role'),
});

type EditMemberFormData = z.infer<typeof editMemberSchema>;

interface EditMemberFormProps {
  bandId: string;
  member: {
    profile: Profile;
    role: string;
    instruments: string[];
  };
  totalAdmins: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export function EditMemberForm({ bandId, member, totalAdmins, onSuccess, onCancel }: EditMemberFormProps) {
  const { t } = useTranslation();
  const { getTranslatedSkills } = useMusicTranslations();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<EditMemberFormData>({
    resolver: zodResolver(editMemberSchema),
    defaultValues: {
      role: member.role as 'admin' | 'member',
      instruments: member.instruments,
    },
  });

  const watchInstruments = watch('instruments', []);
  const watchRole = watch('role');

  const translatedSkills = getTranslatedSkills();

  const onSubmit = async (data: EditMemberFormData) => {
    if (submitting) return;
    
    // Prevent removing the last admin
    if (member.role === 'admin' && data.role === 'member' && totalAdmins <= 1) {
      setError('Cannot change the last admin to member. There must be at least one admin.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');

      const { error: updateError } = await supabase
        .from('band_members')
        .update({
          role: data.role,
          instruments: data.instruments,
        })
        .eq('band_id', bandId)
        .eq('profile_id', member.profile.id);

      if (updateError) throw updateError;
      onSuccess();
    } catch (err) {
      console.error('Error updating member:', err);
      setError('Failed to update member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInstrumentToggle = (instrument: string) => {
    const currentInstruments = watchInstruments;
    const newInstruments = currentInstruments.includes(instrument)
      ? currentInstruments.filter(i => i !== instrument)
      : [...currentInstruments, instrument];
    setValue('instruments', newInstruments);
  };

  const isLastAdmin = member.role === 'admin' && totalAdmins <= 1;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-center">
          {error}
        </div>
      )}

      <div className="flex items-center space-x-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden">
          {member.profile.avatar_url ? (
            <img
              src={member.profile.avatar_url}
              alt={member.profile.artist_name || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-8 h-8 bg-gray-300 rounded-full" />
          )}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-light">
            {member.profile.artist_name || member.profile.full_name}
          </h3>
          <p className="text-sm text-light/60">Edit member details</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-light/80 mb-4">
          Role
        </label>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              value="admin"
              {...register('role')}
              className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
            />
            <span className="text-light/80">Admin</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              value="member"
              {...register('role')}
              disabled={isLastAdmin}
              className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary disabled:opacity-50"
            />
            <span className={`text-light/80 ${isLastAdmin ? 'opacity-50' : ''}`}>Member</span>
          </label>
        </div>
        {isLastAdmin && watchRole === 'member' && (
          <p className="mt-2 text-sm text-yellow-500">
            Cannot change the last admin to member. Promote another member to admin first.
          </p>
        )}
        {errors.role && (
          <p className="mt-2 text-sm text-red-500">{errors.role.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-light/80 mb-4">
          Instruments/Roles
        </label>
        <div className="grid grid-cols-2 gap-2">
          {translatedSkills.map((skill) => (
            <button
              key={skill.value}
              type="button"
              onClick={() => handleInstrumentToggle(skill.value)}
              className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all ${
                watchInstruments.includes(skill.value)
                  ? 'bg-secondary text-light hover:bg-secondary/80'
                  : 'bg-[#232323] text-light/60 hover:bg-[#2a2a2a]'
              }`}
            >
              <span>{skill.label}</span>
            </button>
          ))}
        </div>
        {errors.instruments && (
          <p className="mt-2 text-sm text-red-500">{errors.instruments.message}</p>
        )}
      </div>

      <div className="flex flex-col md:flex-row justify-end md:space-x-4 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline order-2 mt-6 md:mt-0"
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn-secondary md:order-2"
          disabled={submitting}
        >
          {submitting ? (
            <span className="flex items-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Updating...</span>
            </span>
          ) : (
            'Update Member'
          )}
        </button>
      </div>
    </form>
  );
}